import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { sanityFetch, sanityImageUrl } from "../sanityClient";
import EventModal from "./events/EventModal";
import ROUTES from "../routes";

type SanityEvent = {
  id: { current: string };
  title: string;
  description: string;
  dates: string[];
  time: string;
  image?: { asset: { _ref: string } };
  imageUrl?: string;
  admission?: string;
  highlights?: string[];
  tags?: string[];
  tickets?: string;
  paymentLink?: string;
  organizer?: { name?: string; email?: string; phone?: string; website?: string };
};

type EventData = Omit<SanityEvent, "id"> & { id: string; displayDate: string };

type MarketStatus = {
  cancelledDates?: { date: string; reason?: string }[];
};

function daysUntil(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  target.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

function daysLabel(days: number): { text: string; color: string; bg: string } {
  if (days === 0) return { text: "Today!", color: "#c6252d", bg: "rgba(198,37,45,0.18)" };
  if (days === 1) return { text: "Tomorrow", color: "#e07000", bg: "rgba(224,112,0,0.16)" };
  if (days <= 7) return { text: `In ${days} days`, color: "#e07000", bg: "rgba(224,112,0,0.13)" };
  return { text: `In ${days} days`, color: "#6dc744", bg: "rgba(109,199,68,0.12)" };
}

function nextSaturdayDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 6 ? 0 : (6 - day);
  d.setDate(d.getDate() + diff);
  return d;
}

export default function EventAlertPopup() {
  const [events, setEvents] = useState<EventData[]>([]);
  const [visible, setVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  const [saturdayCancellation, setSaturdayCancellation] = useState<{ reason?: string } | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  useEffect(() => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const sat = nextSaturdayDate();
    const satKey = `${sat.getFullYear()}-${pad(sat.getMonth() + 1)}-${pad(sat.getDate())}`;

    sanityFetch<MarketStatus>(
      `*[_type == "marketStatus"][0]{ cancelledDates }`
    ).then((status) => {
      const match = status?.cancelledDates?.find((c) => c.date === satKey);
      if (match) setSaturdayCancellation({ reason: match.reason });
    });

    sanityFetch<SanityEvent[]>(
      `*[_type == "event"]{ id, title, description, dates, time, image, imageUrl, admission, highlights, tags, tickets, paymentLink, organizer }`
    ).then((data) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const candidates = data
        .map((e) => {
          const upcoming = e.dates
            .map((d) => {
              const [y, m, day] = d.split("-").map(Number);
              return new Date(y, m - 1, day);
            })
            .filter((d) => d >= today)
            .sort((a, b) => a.getTime() - b.getTime());
          return { e, soonest: upcoming[0] };
        })
        .filter((x) => x.soonest)
        .sort((a, b) => a.soonest!.getTime() - b.soonest!.getTime())
        .slice(0, 3)
        .map(({ e, soonest }) => {
          const displayDate = `${soonest!.getFullYear()}-${pad(soonest!.getMonth() + 1)}-${pad(soonest!.getDate())}`;
          return { ...e, id: e.id?.current ?? "", displayDate };
        });

      if (!candidates.length) return;
      setEvents(candidates);
      setTimeout(() => setVisible(true), 2500);
    });
  }, []);

  function dismiss() { setVisible(false); }

  if (!events.length) return null;

  const saturday = nextSaturdayDate();
  const isToday = saturday.getDay() === new Date().getDay() && saturday.getDate() === new Date().getDate();
  const saturdayLabel = saturday.toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
  });

  const cardPadding = isMobile ? "1rem 1.1rem 1.2rem" : "1.1rem 1.3rem 1.3rem";

  return (
    <>
      <AnimatePresence>
        {visible && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={dismiss}
              style={{
                position: "fixed", inset: 0,
                background: "rgba(5, 10, 20, 0.72)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                zIndex: 9998,
              }}
            />

            {/* Centering shell */}
            <div
              style={{
                position: "fixed", inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: isMobile ? "flex-end" : "center",
                zIndex: 9999,
                pointerEvents: "none",
                padding: isMobile ? 0 : "0 16px",
              }}
            >
              {/* Popup card */}
              <motion.div
                key="popup"
                initial={{ opacity: 0, y: isMobile ? "100%" : 60, scale: isMobile ? 1 : 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: isMobile ? "100%" : 40, scale: isMobile ? 1 : 0.96 }}
                transition={{ type: "spring", stiffness: 320, damping: 30 }}
                style={{
                  pointerEvents: "auto",
                  width: isMobile ? "100%" : "clamp(340px, 90vw, 480px)",
                  background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 60%, #150308 100%)",
                  borderRadius: isMobile ? "20px 20px 0 0" : 20,
                  overflow: "hidden",
                  boxShadow: "0 -8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(109,199,68,0.12)",
                  position: "relative",
                }}
              >
                {/* Decorative blobs */}
                <div style={{
                  position: "absolute", top: -60, right: -60, width: 220, height: 220,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(198,37,45,0.18) 0%, transparent 70%)",
                  pointerEvents: "none",
                }} />
                <div style={{
                  position: "absolute", bottom: -60, left: -60, width: 200, height: 200,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(109,199,68,0.12) 0%, transparent 70%)",
                  pointerEvents: "none",
                }} />

                {/* Mobile drag handle */}
                {isMobile && (
                  <div style={{ display: "flex", justifyContent: "center", paddingTop: 10, paddingBottom: 2 }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)" }} />
                  </div>
                )}

                {/* Close button */}
                <button
                  onClick={dismiss}
                  aria-label="Close"
                  style={{
                    position: "absolute", top: isMobile ? 12 : 14, right: 14, zIndex: 10,
                    background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: "50%", width: 30, height: 30,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,0.9)", cursor: "pointer", fontSize: "1rem",
                    transition: "background 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(198,37,45,0.35)";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(0,0,0,0.45)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.9)";
                  }}
                >
                  ✕
                </button>

                <div style={{ padding: cardPadding, position: "relative", zIndex: 1 }}>

                  {/* ── THIS SATURDAY section ── */}
                  <div style={{
                    background: saturdayCancellation ? "rgba(198,37,45,0.07)" : "rgba(109,199,68,0.07)",
                    border: `1px solid ${saturdayCancellation ? "rgba(198,37,45,0.22)" : "rgba(109,199,68,0.18)"}`,
                    borderRadius: 14,
                    padding: "0.75rem 1rem",
                    marginBottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.85rem",
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: saturdayCancellation ? "rgba(198,37,45,0.12)" : "rgba(109,199,68,0.12)",
                      border: `1px solid ${saturdayCancellation ? "rgba(198,37,45,0.22)" : "rgba(109,199,68,0.22)"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.25rem",
                    }}>
                      {saturdayCancellation ? "⛈️" : "🌿"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: saturdayCancellation ? "#e84545" : "#6dc744",
                        marginBottom: 2,
                      }}>
                        {saturdayCancellation ? "Market Cancelled" : isToday ? "Open Today!" : "This Saturday"}
                      </div>
                      <div style={{
                        color: "#fff", fontWeight: 700,
                        fontSize: "0.88rem",
                        fontFamily: "'Montserrat', sans-serif",
                        lineHeight: 1.2,
                      }}>
                        {saturdayLabel}
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem", marginTop: 1 }}>
                        {saturdayCancellation
                          ? (saturdayCancellation.reason ?? "Market closed this week")
                          : "9:00 AM – 4:00 PM · Year-Round"}
                      </div>
                    </div>
                    <div style={{
                      background: saturdayCancellation ? "rgba(198,37,45,0.18)" : isToday ? "rgba(109,199,68,0.2)" : "rgba(109,199,68,0.1)",
                      border: `1px solid ${saturdayCancellation ? "rgba(198,37,45,0.4)" : `rgba(109,199,68,${isToday ? "0.4" : "0.2"})`}`,
                      borderRadius: 999, padding: "3px 9px",
                      color: saturdayCancellation ? "#e84545" : "#6dc744",
                      fontSize: "0.65rem", fontWeight: 700,
                      letterSpacing: "0.06em", flexShrink: 0,
                    }}>
                      {saturdayCancellation ? "● CLOSED" : isToday ? "● OPEN" : "● WEEKLY"}
                    </div>
                  </div>

                  {/* ── UPCOMING EVENTS section ── */}
                  <div style={{
                    fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: "rgba(255,255,255,0.35)",
                    marginBottom: "0.55rem",
                  }}>
                    Upcoming Events
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", marginBottom: "0.9rem" }}>
                    {events.map((ev) => {
                      const days = daysUntil(ev.displayDate);
                      const badge = daysLabel(days);
                      const imgSrc = ev.image?.asset ? sanityImageUrl(ev.image) : ev.imageUrl;
                      const formattedDate = (() => {
                        const [y, m, d] = ev.displayDate.split("-").map(Number);
                        return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
                          weekday: "short", month: "short", day: "numeric",
                        });
                      })();

                      return (
                        <button
                          key={ev.id}
                          onClick={() => { setVisible(false); setSelectedEvent(ev); }}
                          style={{
                            display: "flex", alignItems: "center", gap: "0.7rem",
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 12, padding: "0.55rem 0.7rem",
                            cursor: "pointer", textAlign: "left", width: "100%",
                            transition: "background 0.18s, border-color 0.18s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                          }}
                        >
                          {/* Thumbnail */}
                          <div style={{
                            width: 44, height: 44, borderRadius: 8, flexShrink: 0, overflow: "hidden",
                            background: "rgba(109,199,68,0.1)",
                            border: "1px solid rgba(109,199,68,0.15)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {imgSrc
                              ? <img src={imgSrc} alt={ev.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <span style={{ fontSize: "1.2rem" }}>🎪</span>
                            }
                          </div>

                          {/* Text */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: "#fff", fontWeight: 700,
                              fontSize: "0.82rem",
                              fontFamily: "'Montserrat', sans-serif",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              marginBottom: 2,
                            }}>
                              {ev.title}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
                              {formattedDate}
                            </div>
                          </div>

                          {/* Days badge + arrow */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            <span style={{
                              background: badge.bg,
                              border: `1px solid ${badge.color}40`,
                              borderRadius: 999, padding: "2px 8px",
                              color: badge.color, fontSize: "0.65rem", fontWeight: 700,
                              letterSpacing: "0.04em", whiteSpace: "nowrap",
                            }}>
                              {badge.text}
                            </span>
                            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem" }}>›</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Footer ── */}
                  <div style={{ height: 1, background: "rgba(255,255,255,0.07)", marginBottom: "0.85rem" }} />

                  <Link
                    to={ROUTES.SEE_EVENTS}
                    onClick={dismiss}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: "100%",
                      background: "rgba(109,199,68,0.1)",
                      border: "1px solid rgba(109,199,68,0.25)",
                      borderRadius: 999,
                      padding: isMobile ? "0.7rem" : "0.6rem",
                      color: "#6dc744", fontWeight: 700,
                      fontSize: "0.88rem",
                      fontFamily: "'Montserrat', sans-serif",
                      textDecoration: "none",
                      transition: "background 0.2s, border-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(109,199,68,0.18)";
                      e.currentTarget.style.borderColor = "rgba(109,199,68,0.45)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(109,199,68,0.1)";
                      e.currentTarget.style.borderColor = "rgba(109,199,68,0.25)";
                    }}
                  >
                    See All Events →
                  </Link>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {selectedEvent && (
        <EventModal
          show={!!selectedEvent}
          onHide={() => setSelectedEvent(null)}
          event={selectedEvent}
        />
      )}
    </>
  );
}
